import styled from '@emotion/styled'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRecoilState } from 'recoil'

import Modal from '@/components/Modal'
import useGetRecoilState from '@/hooks/useGetRecoilState'
import { audioLiveState, roomWindowScaleState, rtmUserState, screenSharingState, useDeviceState } from '@/store/room'
import { BodyType, ipcInvoke, ipcOn, isElectron } from '@/utils/electron'

import { rtcClient, rtmClient } from '../..'

// import { globalHeaderVisibleState } from '@/store/global'

// 定义Electron类型
interface IpcRendererEvent {
  sender: any
  senderId: number
}

interface DisplaysType {
  id: number
  width: number
  height: number
  x: number
  y: number
}

export default function Index() {
  const recorder = useRef<any>(null)
  const [useDevice, setUseDevice] = useRecoilState(useDeviceState)
  const [screenShareTips, setScreenShareTips] = useState(false)

  const [screenSharing, setScreenSharing] = useRecoilState(screenSharingState)
  const [roomWindowScale, setRoomWindowScale] = useRecoilState(roomWindowScaleState)
  const [audioLive, setAudioLive, getAudioLive] = useGetRecoilState(audioLiveState)
  const [rtmUser, setRtmUser, getRtmUser] = useGetRecoilState(rtmUserState)
  // const [, setGlobalHeaderVisible] = useRecoilState(globalHeaderVisibleState)

  useEffect(() => {
    ipcOn('msg', onMsg)

    rtmClient.on('ArtScreenShareControl', onArtScreenShare)
    return () => {
      rtmClient.removeListener('ArtScreenShareControl', onArtScreenShare)
    }
  }, [])

  const onArtScreenShare = (id: number, payload: any) => {
    console.log('🚀 ~ onArtScreenShare ~ data:', payload)
    setScreenShareTips(payload?.isShow)
  }

  const onMsg = useCallback(async (event: any, result: BodyType, ...args: any[]) => {
    const { data } = result
    console.log('🚀 ~ onMsg ~ result:', result)

    if ('loaded' in data) {
      ipcInvoke('custom', {
        method: 'msg',
        toWinName: 'screenRecordControl',
        data: {
          isMute: !getAudioLive().mainStudent
        }
      })
    }

    if ('setMute' in data) {
      if (data.setMute) {
        await rtcClient?.deviceOn('audio')
      } else {
        await rtcClient?.deviceOff('audio')
      }
    }

    if ('setOpenVideo' in data) {
      if (data.setOpenVideo) {
        await rtcClient?.deviceOff('mainVideo')
      } else {
        await rtcClient?.deviceOn('mainVideo')
      }
      ipcInvoke('custom', {
        method: 'msg',
        toWinName: 'screenRecordControl',
        data: {
          openVideo: !data.setOpenVideo
        }
      })
    }

    if ('exit' in data) {
      overScreenShare()
    }
  }, [])

  useEffect(() => {
    ipcInvoke('custom', {
      method: 'msg',
      toWinName: 'screenRecordControl',
      data: {
        isMute: !audioLive.mainStudent
      }
    })
  }, [audioLive.mainStudent])

  // 获取屏幕
  const getScreen = async () => {
    try {
      // 通过IPC调用主进程获取
      const screenViaIPC: any = await ipcInvoke('DESKTOP_CAPTURER_GET_SOURCES', {
        method: 'getSources',
        data: { types: ['screen', 'window'] } // 同时获取屏幕和窗口
      })
      if (Array.isArray(screenViaIPC?.data)) {
        const [screen] = screenViaIPC.data
        startScreenShare(screen)
      } else {
        console.error('没有可用的屏幕源')
      }
    } catch (error) {
      console.error('获取屏幕源时出错:', error)
    }
  }

  const startScreenShare = async (screen: any) => {
    const { id, bounds } = screen
    recorder.current = useDevice
    if (rtmUser) {
      const diviceName = rtmUser.deviceName.split(' ')[0] ?? 'mac'
      if (['windows', 'mac', 'linux'].includes(diviceName)) {
        setUseDevice({ ...useDevice, viceCamera: '' })
        await rtcClient.changeViceCamera({ deviceId: '', publish: false })
        await rtcClient.changeViceCamera({ deviceId: id, publish: true, screen })
      } else {
        setUseDevice({ ...useDevice, mainCamera: '' })

        await rtcClient.changeMainCamera({ deviceId: '', publish: false })
        await rtcClient.changeMainCamera({ deviceId: id, publish: true, screen })
      }
    } else {
      setUseDevice({ ...useDevice, viceCamera: '' })
      await rtcClient.changeViceCamera({ deviceId: '', publish: false })
      await rtcClient.changeViceCamera({ deviceId: id, publish: true, screen })
    }
    rtmClient.sendMessage('ArtScreenShareControl', {
      isShow: true
    })

    ipcInvoke('custom', {
      method: 'create-win',
      data: {
        ...bounds,
        newWinName: 'screenRecordMark',
        hasShadow: false,
        transparent: true,
        skipTaskbar: true,
        hiddenInMissionControl: true,
        roundedCorners: false,
        fullscreen: true,
        alwaysOnTop: true, // 窗口总是在其他窗口之上
        titleBarStyle: 'hidden', // 隐藏标题栏（菜单栏之下）
        path: window.location.origin + '/student/screenRecordMark'
      }
    })
    ipcInvoke('custom', {
      method: 'create-win',
      data: {
        newWinName: 'screenRecordControl',
        skipTaskbar: true, // 不在任务栏中展示
        hiddenInMissionControl: true, // 切换任务控制隐藏
        alwaysOnTop: true, // 窗口总是在其他窗口之上
        width: 300,
        height: 120,
        y: 10,
        x: localStorage.screenWidth / 2 - 150,
        resizable: false,
        frame: false,
        transparent: true,
        hasShadow: false,
        path: window.location.origin + '/student/screenRecordControl'
      }
    })
    setScreenSharing(true)
    setRoomWindowScale('1X')
    ipcInvoke('win', { method: 'setAlwaysOnTop' }, true, 'main-menu')
  }

  const overScreenShare = async () => {
    ipcInvoke('win', { method: 'setAlwaysOnTop' }, false)
    setScreenSharing(false)
    setRoomWindowScale('full')
    console.log('🚀 ~ overScreenShare ~ rtmUser:', getRtmUser())
    const getUser = getRtmUser()

    if (getUser) {
      const diviceName = getUser.deviceName.split(' ')[0] ?? 'mac'
      if (['windows', 'mac', 'linux'].includes(diviceName)) {
        await rtcClient.changeViceCamera({ deviceId: '', publish: false, screen: {} })
      } else {
        await rtcClient.changeMainCamera({ deviceId: '', publish: false, screen: {} })
      }
    } else {
      await rtcClient.changeViceCamera({ deviceId: '', publish: false, screen: {} })
      await rtcClient.changeMainCamera({ deviceId: '', publish: false, screen: {} })
    }

    const { mainCamera, viceCamera } = recorder.current ?? {}
    console.log('🚀 ~ overScreenShare ~ recorder.current :', recorder.current)

    const timer = setTimeout(() => {
      if (mainCamera) {
        rtcClient.changeMainCamera({ deviceId: mainCamera, publish: true })
      }

      if (viceCamera) {
        rtcClient.changeViceCamera({ deviceId: viceCamera, publish: true })
      }
      setUseDevice({ ...recorder.current })
      clearTimeout(timer)
    }, 500)
  }

  return (
    <>
      {/* <div onClick={() => (screenSharing ? setRoomWindowScale('1X') : getScreen())} className="anim_btn2 w-[5vh] h-[5vh] screen"></div> */}

      <Modal
        show={screenShareTips && !screenSharing && isElectron()}
        title="请求打开屏幕共享"
        cancel={() => setScreenShareTips(false)}
        confirm={() => {
          setScreenShareTips(false)
          getScreen()
        }}
      >
        <div className="text-[#333] text-center">老师已共享屏幕，快打开你的屏幕共享</div>
        <div className="text-[#333] text-center mb-[20px]">方便老师精准指导，高效快乐学习</div>
      </Modal>
    </>
  )
}

const Container = styled(motion.div)``
